import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, ThemeProvider, useAuth } from '@/hooks';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { BrandMark } from '@/components/brand';
import { AuthLayout, DashboardLayout } from '@/layouts';
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  DashboardPage,
  OrganizationsPage,
  OrganizationDetailPage,
  OrganizationNewPage,
  ProductsPage,
  ProductNewPage,
  ProductDetailPage,
  ProductEditPage,
  ClientsPage,
  ClientNewPage,
  ClientDetailPage,
  ClientEditPage,
  MyAccessPage,
  SettingsPage,
  UsersPage,
  UserDetailPage,
  AccessMatrixPage,
  AuditPage,
  KeysPage,
  DocsPage,
  PlaygroundPage,
  PlaygroundCallbackPage,
  McpAccessPage,
} from '@/pages';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <AppLoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <AppLoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function SuperAdminRoute({
  children,
  redirectTo = '/dashboard',
}: {
  children: React.ReactNode;
  redirectTo?: string;
}) {
  const { user } = useAuth();
  return user?.is_super_admin ? <>{children}</> : <Navigate to={redirectTo} replace />;
}

function AppLoadingScreen() {
  return (
    <div className="auth-surface flex min-h-screen items-center justify-center px-4">
      <div className="panel flex flex-col items-center gap-4 rounded-2xl px-8 py-7">
        <BrandMark />
        <div className="h-1.5 w-40 overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/2 animate-pulse rounded-full bg-primary" />
        </div>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes (redirect to dashboard if logged in) */}
      <Route
        element={
          <PublicRoute>
            <AuthLayout />
          </PublicRoute>
        }
      >
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
      </Route>

      {/* Public routes (accessible regardless of auth status) */}
      <Route element={<AuthLayout />}>
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/organizations" element={<OrganizationsPage />} />
        <Route path="/organizations/new" element={<OrganizationNewPage />} />
        <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
        <Route path="/organizations/:id/edit" element={<OrganizationDetailPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route
          path="/products/new"
          element={
            <SuperAdminRoute redirectTo="/products">
              <ProductNewPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/products/:id"
          element={
            <SuperAdminRoute redirectTo="/products">
              <ProductDetailPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/products/:id/edit"
          element={
            <SuperAdminRoute redirectTo="/products">
              <ProductEditPage />
            </SuperAdminRoute>
          }
        />
        <Route path="/clients" element={<ClientsPage />} />
        <Route
          path="/clients/new"
          element={
            <SuperAdminRoute redirectTo="/clients">
              <ClientNewPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/clients/:id"
          element={
            <SuperAdminRoute redirectTo="/clients">
              <ClientDetailPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/clients/:id/edit"
          element={
            <SuperAdminRoute redirectTo="/clients">
              <ClientEditPage />
            </SuperAdminRoute>
          }
        />
        <Route path="/access" element={<MyAccessPage />} />
        <Route
          path="/users"
          element={
            <SuperAdminRoute>
              <UsersPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/users/:id"
          element={
            <SuperAdminRoute>
              <UserDetailPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/access-matrix"
          element={
            <SuperAdminRoute>
              <AccessMatrixPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <SuperAdminRoute>
              <AuditPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/keys"
          element={
            <SuperAdminRoute>
              <KeysPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/mcp-access"
          element={
            <SuperAdminRoute>
              <McpAccessPage />
            </SuperAdminRoute>
          }
        />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/playground" element={<PlaygroundPage />} />
        <Route path="/playground/callback" element={<PlaygroundCallbackPage />} />
      </Route>

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <TooltipProvider>
              <AppRoutes />
              <Toaster />
            </TooltipProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
